/**
 * Reviews page gallery (sections/reviews-gallery.liquid).
 * A click on a screenshot opens it full screen; arrows, the keyboard and swipes move between
 * screenshots, and a click outside the screenshot or Esc closes it.
 */
class ReviewsGallery extends HTMLElement {
  /** @type {HTMLAnchorElement[]} */
  #links = [];
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLImageElement | null} */
  #image = null;
  #index = 0;
  /** @type {{ x: number, y: number } | null} */
  #pointerStart = null;
  #swiped = false;

  connectedCallback() {
    this.#links = [...this.querySelectorAll('a.reviews-gallery__link')].filter(
      (link) => link instanceof HTMLAnchorElement
    );
    this.#dialog = this.querySelector('dialog.reviews-lightbox');
    if (!this.#dialog || !this.#links.length) return;

    this.addEventListener('click', this.#onClick);
    this.#dialog.addEventListener('keydown', this.#onKeydown);
    this.#dialog.addEventListener('pointerdown', this.#onPointerDown);
    this.#dialog.addEventListener('pointerup', this.#onPointerUp);
    this.#dialog.addEventListener('pointercancel', this.#onPointerCancel);
    this.#dialog.addEventListener('close', this.#onClose);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#dialog?.removeEventListener('keydown', this.#onKeydown);
    this.#dialog?.removeEventListener('pointerdown', this.#onPointerDown);
    this.#dialog?.removeEventListener('pointerup', this.#onPointerUp);
    this.#dialog?.removeEventListener('pointercancel', this.#onPointerCancel);
    this.#dialog?.removeEventListener('close', this.#onClose);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const link = target?.closest('a.reviews-gallery__link');

    if (link instanceof HTMLAnchorElement && this.#links.includes(link)) {
      event.preventDefault();
      this.#open(this.#links.indexOf(link));
      return;
    }

    if (!this.#dialog?.open) return;

    // A mouse drag ends with a click; don't let it close the dialog
    if (this.#swiped) {
      this.#swiped = false;
      return;
    }

    const stepButton = target?.closest('[data-step]');
    if (stepButton instanceof HTMLElement) {
      this.#show(this.#index + Number(stepButton.dataset.step));
    } else if (target === this.#dialog || target?.closest('[data-close]')) {
      this.#dialog.close();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.#show(this.#index - 1);
    if (event.key === 'ArrowRight') this.#show(this.#index + 1);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#swiped = false;
    this.#pointerStart = { x: event.clientX, y: event.clientY };
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#pointerStart) return;

    const deltaX = event.clientX - this.#pointerStart.x;
    const deltaY = event.clientY - this.#pointerStart.y;
    this.#pointerStart = null;
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) < Math.abs(deltaY)) return;

    this.#swiped = true;
    this.#show(this.#index + (deltaX < 0 ? 1 : -1));
  };

  #onPointerCancel = () => {
    this.#pointerStart = null;
  };

  #onClose = () => {
    document.documentElement.removeAttribute('scroll-lock');
    this.#links[this.#index]?.focus();
  };

  /** @param {number} index */
  #open(index) {
    if (!this.#dialog) return;

    if (!this.#image) {
      this.#image = document.createElement('img');
      this.#image.className = 'reviews-lightbox__image';
      this.#image.decoding = 'async';
      // A mouse drag would otherwise start dragging the picture instead of a swipe
      this.#image.draggable = false;
      this.#dialog.prepend(this.#image);
    }

    // A video playing next to the photos (the kit packaging galleries) stops while a photo is open
    for (const video of this.querySelectorAll('video')) video.pause();

    this.#show(index);
    document.documentElement.setAttribute('scroll-lock', '');
    this.#dialog.showModal();
  }

  /** @param {number} index */
  #show(index) {
    const count = this.#links.length;
    this.#index = (index + count) % count;

    const link = this.#links[this.#index];
    if (!link || !this.#image) return;

    this.#image.width = Number(link.dataset.width) || 0;
    this.#image.height = Number(link.dataset.height) || 0;
    this.#image.src = link.href;
    this.#image.alt = link.querySelector('img')?.alt ?? '';

    const counter = this.#dialog?.querySelector('.reviews-lightbox__counter');
    if (counter) counter.textContent = `${this.#index + 1} / ${count}`;

    // Load the neighbours ahead, so the next swipe shows the screenshot at once
    for (const offset of [-1, 1]) {
      const neighbour = this.#links[(this.#index + offset + count) % count];
      if (neighbour) new Image().src = neighbour.href;
    }
  }
}

if (!customElements.get('reviews-gallery')) {
  customElements.define('reviews-gallery', ReviewsGallery);
}
